import React, { useMemo, useState } from 'react';
import { Mail, AlertCircle } from 'lucide-react';

interface EmailListFieldProps {
  label: string;
  /** Cadena con uno o varios emails separados por ';'. */
  value: string;
  onChange: (next: string) => void;
}

// Regex pragmática equivalente a la del backend (EmailListValidator.cs).
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const parseTokens = (raw: string): string[] =>
  raw
    .split(';')
    .map(t => t.trim())
    .filter(t => t.length > 0);

/**
 * Input de texto para una lista de emails separados por punto y coma. Mientras
 * el usuario escribe no interrumpimos; al perder el foco validamos cada token
 * y mostramos chips legibles + lista de inválidos. La validación viva contra
 * el backend (mismo regex) evita ida y vuelta al servidor para errores típicos.
 */
export const EmailListField: React.FC<EmailListFieldProps> = ({ label, value, onChange }) => {
  const [touched, setTouched] = useState(false);

  const tokens = useMemo(() => parseTokens(value), [value]);
  const invalid = useMemo(
    () => tokens.filter(t => !EMAIL_RX.test(t)),
    [tokens],
  );

  const showError = touched && invalid.length > 0;

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
        <Mail size={14} className="text-slate-400" />
        {label}
        <span className="text-xs font-normal text-slate-400">
          (separa varios con punto y coma)
        </span>
      </label>
      <input
        type="text"
        inputMode="email"
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder="ejemplo@dominio.com; otro@dominio.com"
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm ${
          showError ? 'border-red-400' : 'border-slate-300'
        }`}
      />

      {/* Vista previa en chips para que el usuario vea bien dónde rompió */}
      {tokens.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tokens.map((t, i) => {
            const ok = EMAIL_RX.test(t);
            return (
              <span
                key={`${t}-${i}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${
                  ok
                    ? 'bg-slate-100 text-slate-700 border-slate-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}
                title={ok ? '' : 'Formato inválido'}
              >
                {!ok && <AlertCircle size={11} />}
                {t}
              </span>
            );
          })}
        </div>
      )}

      {showError && (
        <p className="mt-1 text-xs text-red-600">
          {invalid.length === 1
            ? `"${invalid[0]}" no es un email válido.`
            : `${invalid.length} emails con formato inválido. Revisa los marcados en rojo.`}
        </p>
      )}
    </div>
  );
};
